// polyfill
import '@babel/polyfill';
import 'intersection-observer';
import 'core-js/fn/array/flatten';
import 'focus-visible';

import React from 'react';
import { hydrate } from 'react-dom';
import { Router } from 'react-router';
import { createBrowserHistory } from 'history';
import { Provider } from 'react-redux';
import { loadableReady } from '@loadable/component';
import { StyledTheme } from '@prisjakt/ui-base';
import after from 'lodash.after';

import translator from '@prisjakt/ui-translate/src/translator';
import { AppContainer } from 'react-hot-loader'; // eslint-disable-line

import './public-path';
import { AppContextContainer } from '@prisjakt/ui-app-context';
import throttle from 'lodash.throttle';
import addPerformanceTiming from './performance-reporter';
import store from './store';
import FetchContext from '../components/fetch-context';
import { ViewportContextContainer } from '../components/viewport/context';
import { sentryPromise } from './sentry';
import { AdContextContainer } from '../components/ads/context';
import AdServiceContext from '../components/ads/services/context';
import App from './hot-app';
import ExperimentContextContainer from './experiments';
import {
  loadLocalStorageState,
  saveStateToLocalStorage,
} from './local-storage';

import query from './query';

const history = createBrowserHistory();

// eslint-disable-next-line no-underscore-dangle
window.__history = history;

// eslint-disable-next-line no-underscore-dangle
window.__getState = store.getState;

window.APP_GLOBALS = {
  t: translator(window.TRANSLATIONS, window.CLIENT_MARKET.locale),
};

/*
window.onerror = error => {
  console.log('UNCAUGHT ERROR', error);
  return false;
};
*/

class AppWithViewport extends React.Component {
  state = {
    initialRender: window.SERVER_SIDE_RENDER_ROUTES === false,
  };

  fetchContextValue = {
    query,
    ...window.CONTEXTS.fetch,
  };

  componentDidMount() {
    if (this.state.initialRender) {
      this.setState({
        initialRender: false,
      });
    }
  }

  render() {
    return (
      <AppContextContainer
        renderMode={window.RENDER_CONTEXT_MODE}
        market={window.CLIENT_MARKET}
        locale={window.LOCALE}
        translations={window.TRANSLATIONS}
        sentryPromise={sentryPromise}
      >
        <StyledTheme>
          <FetchContext.Provider value={this.fetchContextValue}>
            <Provider store={store}>
              <AdContextContainer>
                <AdServiceContext.Provider>
                  <ViewportContextContainer>
                    <ExperimentContextContainer>
                      <Router history={history}>
                        <App
                          renderRoutes={
                            window.SERVER_SIDE_RENDER_ROUTES === true ||
                            this.state.initialRender === false
                          }
                        />
                      </Router>
                    </ExperimentContextContainer>
                  </ViewportContextContainer>
                </AdServiceContext.Provider>
              </AdContextContainer>
            </Provider>
          </FetchContext.Provider>
        </StyledTheme>
      </AppContextContainer>
    );
  }
}

addPerformanceTiming('startHydration');
const renderApp = (TheApp, domNode) =>
  hydrate(
    <AppWithViewport />,
    domNode || window.document.getElementById('root'),
    () => {
      addPerformanceTiming('hydrationCompleted');
      const { me, session, trackingSettings, gaClientId } = window.SESSION_DATA;

      window.PRE_TRACK_CLICKS = false;

      store.dispatch({
        type: 'SESSION_RECEIVE',
        session,
        privacyCookie: trackingSettings,
        clientId: gaClientId,
      });

      if (me) {
        store.dispatch({
          type: 'USER_RECEIVE',
          me,
        });
      }

      store.dispatch({
        type: 'APP_STARTUP',
        market: window.CLIENT_MARKET.code,
        release: window.RELEASE,
      });

      loadLocalStorageState(store);

      store.subscribe(
        throttle(() => {
          saveStateToLocalStorage(store);
        }, 1000)
      );

      if (/[&?]_verify_complete\b/.test(window.location.search)) {
        const paramValue = window.location.search.match(
          /[&?]_verify_complete=([^&]+)/
        );
        const mode = paramValue.length > 1 ? paramValue[1] : null;
        store.dispatch({
          type: 'VERIFY_ACCOUNT_COMPLETE',
          mode,
        });
      }
    }
  );

if (!window.TRANSLATIONS) {
  throw new Error('Missing translations, aborting hydrate');
}

const done = after(2, function() {
  renderApp(App);
});

window.sessionReady(done);
loadableReady(done);
